import { useState, useCallback } from 'react';
import { fetchWithAuth, getCSRFToken } from '../../auth';

export const useAssetProblems = () => {
  const [assetProblems, setAssetProblems] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadMaintenanceData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const url = `${process.env.REACT_APP_ICC_TOOL_API_HOST}maintenance/dashboard/`;
      const response = await fetchWithAuth(url, {
        headers: { 'X-CSRFToken': getCSRFToken() },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch maintenance dashboard: ${response.statusText}`);
      }

      const data = await response.json();
      setAssetProblems(data.problems || []);
      setPermissions(data.permissions || {});
    } catch (err) {
      setError(`Failed to load maintenance data: ${err.message}`);
      console.error('Error loading maintenance data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get statistics for the current data
   */
  const getStatistics = useCallback(() => {
    const problems = Array.isArray(assetProblems) ? assetProblems : (assetProblems.problems || []);

    const total = problems.length;
    const unresolved = total;
    const outOfService = problems.filter(item => item.assetStatus == 'outOfService').length;
    const maintenance = problems.filter(item => item.assetStatus == 'maintenance').length;

    return {
      total,
      unresolved,
      outOfService,
      maintenance
    };
  }, [assetProblems]);

  /**
   * Clear error and success messages
   */
  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  /**
   * Set success message
   */
  const setSuccessMessage = useCallback((message) => {
    setSuccess(message);
    setError('');
  }, []);

  /**
   * Set error message
   */
  const setErrorMessage = useCallback((message) => {
    setError(message);
    setSuccess('');
  }, []);

  return {
    assetProblems,
    permissions,
    loading,
    error,
    success,
    loadMaintenanceData,
    getStatistics,
    clearMessages,
    setSuccessMessage,
    setErrorMessage
  };
};

export default useAssetProblems;
